import styled from '@emotion/styled'
import { ApolloError } from 'apollo-client'
import dayjs from 'dayjs'
import gql from 'graphql-tag'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from 'react-apollo'
import toastr from 'toastr'
import type { FlyerCandidate, FlyerDuplicate, FlyerImage } from '../../../api/events/flyerPrefill'
import { useMutation } from '../../../api/graphql'
import { MEDIUM } from '../../../styles/Grid'
import cloudinaryTools from '../../../util/cloudinaryTools'
import FileDropBox from '../misc/FileDropBox'

// "Start from a flyer" on the new-event form (CVL-173): the person uploads a flyer they
// already have, the server reads it in the background (CVL-172), and the form fills in what
// was found for them to check. The form stays usable the whole time.

const FLYER_EXTRACTION_CREATE = gql`
  mutation FlyerExtractionCreate($sourcePublicId: String!, $primaryCommunityId: ID) {
    flyerExtractionCreate(sourcePublicId: $sourcePublicId, primaryCommunityId: $primaryCommunityId) {
      flyerExtraction {
        id
        status
      }
      errors {
        attribute
        message
      }
    }
  }
`

const FLYER_EXTRACTION = gql`
  query FlyerExtraction($id: ID!) {
    flyerExtraction(id: $id) {
      id
      status
      failureReason
      classification
      eventCount
      imagePublicId
      imageWidth
      imageHeight
      candidates
    }
  }
`

const POLL_INTERVAL_MS = 2000
const READ_TIMEOUT_MS = 90 * 1000
const FINISHED = ['succeeded', 'failed']
const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,application/pdf,.pdf'
const INPUT_ID = 'flyerStartInput'

const READING = 'Reading your flyer…'
const NOT_READ = "We couldn't read this flyer. Please fill in the form below."
const LIMIT_REACHED = "You've read a lot of flyers today. Please fill in the form below."
const FILLED_IN = 'Filled in from your flyer'
// The one-line summary when the flyer did not fill in the form.
const NOT_FILLED_IN = 'Your flyer'

// What the panel says about a flyer that is not simply one event (CVL-177).
const PROMOTION = "This looks like a business promotion. Your community's curator decides whether promotions are listed."
const NOT_AN_EVENT = "This doesn't look like an event, so we didn't fill in the form."
const SAFETY = 'This may not meet community guidelines.'
const ALREADY_LISTED = 'This might already be listed:'
const severalEvents = (count: number) =>
  `This flyer lists ${count} events. We filled in the first one. Submit it, then start another for the rest.`

type Phase = 'idle' | 'uploading' | 'reading' | 'read' | 'notRead' | 'limitReached'

type Notices = {
  // False when the flyer was read but nothing was filled in: not an event, or a promotion with no event in it.
  filled: boolean
  promotion: boolean
  notAnEvent: boolean
  eventCount: number
  duplicates: FlyerDuplicate[]
  safety: boolean
}

type Upload = FlyerImage & { isPdf: boolean }

type FlyerExtraction = {
  id: string
  status: string
  classification?: string | null
  eventCount?: number | null
  imagePublicId?: string | null
  imageWidth?: number | null
  imageHeight?: number | null
  candidates?: FlyerCandidate[] | null
}

type Props = {
  primaryCommunityId?: string | null
  // The event started saving: a read that finishes after that is ignored.
  stopped: boolean
  onReadStarted: (flyerExtractionId: string) => void
  onRead: (candidate: FlyerCandidate, image: FlyerImage | null) => void
  // A failed or timed-out read, or the daily limit: the image flyer is still the event image.
  onNotRead: (image: FlyerImage | null) => void
}

const isLimitReached = (error: unknown): boolean =>
  error instanceof ApolloError && error.graphQLErrors.some(graphQLError => graphQLError.extensions?.code === 'LIMIT_REACHED')

// A PDF has no picture of its own to use until the read makes one of its first page.
const imageOf = (upload: Upload | null): FlyerImage | null =>
  upload && !upload.isPdf ? { publicId: upload.publicId, width: upload.width, height: upload.height } : null

const FlyerStartPanel: React.FC<Props> = ({ primaryCommunityId, stopped, onReadStarted, onRead, onNotRead }) => {
  const [phase, setPhase] = useState<Phase>('idle')
  const [extractionId, setExtractionId] = useState<string | null>(null)
  const [notices, setNotices] = useState<Notices | null>(null)
  // On a phone the panel shrinks to one line once a read has finished, until the person opens it.
  const [opened, setOpened] = useState(false)
  const upload = useRef<Upload | null>(null)
  const delivery = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => () => clearTimeout(delivery.current), [])
  const [createExtraction] = useMutation(FLYER_EXTRACTION_CREATE)

  const giveUp = useCallback((nextPhase: Phase, image: FlyerImage | null = imageOf(upload.current)) => {
    setPhase(nextPhase)
    setOpened(false)
    onNotRead(image)
  }, [onNotRead])

  const startRead = useCallback(
    async (sourcePublicId: string) => {
      try {
        const { data } = await createExtraction({ variables: { sourcePublicId, primaryCommunityId: primaryCommunityId || null } })
        const id = data?.flyerExtractionCreate?.flyerExtraction?.id
        if (!id) return giveUp('notRead')
        onReadStarted(id)
        setExtractionId(id)
        setPhase('reading')
      } catch (error) {
        giveUp(isLimitReached(error) ? 'limitReached' : 'notRead')
      }
    },
    [createExtraction, giveUp, onReadStarted, primaryCommunityId],
  )

  const uploadFlyer = useCallback(
    (file?: File | null) => {
      if (!file || phase === 'uploading' || phase === 'reading') return
      setPhase('uploading')
      setNotices(null)
      cloudinaryTools.upload(file, { folder: 'events', allowPdf: true }, (error, response) => {
        if (error) {
          toastr.error(error)
          setPhase('idle')
          return
        }
        const uploaded = response as { public_id: string; format?: string; width?: number; height?: number }
        upload.current = {
          publicId: uploaded.public_id,
          width: uploaded.width ?? null,
          height: uploaded.height ?? null,
          isPdf: uploaded.format === 'pdf' || /\.pdf$/i.test(file.name) || file.type === 'application/pdf',
        }
        startRead(uploaded.public_id)
      })
    },
    [phase, startRead],
  )

  const reading = phase === 'reading' && !stopped
  // Not the project's useQuery wrapper: it would split the candidates' snake_case keys
  // (start_date) into nested objects the way it does for record fields.
  const { data, error, stopPolling } = useQuery<{ flyerExtraction?: FlyerExtraction }>(FLYER_EXTRACTION, {
    variables: { id: extractionId },
    skip: !extractionId,
    fetchPolicy: 'network-only',
    pollInterval: reading ? POLL_INTERVAL_MS : 0,
  })

  useEffect(() => {
    if (!reading) return
    const timeout = setTimeout(() => {
      stopPolling?.()
      giveUp('notRead')
    }, READ_TIMEOUT_MS)
    return () => clearTimeout(timeout)
    // The 90 seconds count from the start of the read, not from each poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reading, extractionId])

  useEffect(() => {
    if (stopped) stopPolling?.()
  }, [stopped, stopPolling])

  // A poll the server turns away or the network drops counts as a failed read.
  useEffect(() => {
    if (!reading || !error) return
    stopPolling?.()
    giveUp('notRead')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, reading])

  // While a new read starts, the query still holds the previous read's answer for a moment.
  const extraction = data?.flyerExtraction?.id === extractionId ? data?.flyerExtraction : null
  useEffect(() => {
    if (!reading || !extraction || !FINISHED.includes(extraction.status)) return
    stopPolling?.()
    const candidate = extraction.candidates?.[0]
    const image = extraction.imagePublicId
      ? { publicId: extraction.imagePublicId, width: extraction.imageWidth ?? null, height: extraction.imageHeight ?? null }
      : imageOf(upload.current)
    // Not an event, or a promotion with no event in it: nothing is filled in, but the flyer
    // is still the event image.
    const notAnEvent = extraction.classification === 'not_event'
    const promotionOnly = extraction.classification === 'promotion' && !candidate
    if (extraction.status === 'succeeded' && (notAnEvent || promotionOnly)) {
      setNotices({ filled: false, promotion: promotionOnly, notAnEvent, eventCount: 0, duplicates: [], safety: false })
      return giveUp('read', image)
    }
    if (extraction.status !== 'succeeded' || !candidate) return giveUp('notRead')

    setPhase('read')
    setOpened(false)
    setNotices({
      filled: true,
      promotion: extraction.classification === 'promotion',
      notAnEvent: false,
      eventCount: Math.max(extraction.eventCount || 0, extraction.candidates?.length || 0),
      duplicates: (candidate.possible_duplicates || []).filter(duplicate => duplicate?.url),
      safety: candidate.safety_flagged === true,
    })
    // Filled in after this effect: the form reacts to some changes (picking "Someone else"
    // clears the organizer) once React's batch of updates ends, which would undo the later ones.
    delivery.current = setTimeout(() => onRead(candidate, image), 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraction, reading])

  const message = { uploading: READING, reading: READING, notRead: NOT_READ, limitReached: LIMIT_REACHED }[phase]
  const finished = phase === 'read' || phase === 'notRead' || phase === 'limitReached'
  const collapsed = finished && !opened
  const filledIn = phase === 'read' && notices?.filled === true

  return (
    <StyledWrapper>
      <StyledPanel onDropped={uploadFlyer} className={collapsed ? 'is-collapsed' : undefined}>
        {collapsed ? (
          <div className="flyer-start-summary">
            <span>{filledIn ? FILLED_IN : NOT_FILLED_IN}</span>
            <button type="button" className="flyer-start-open" onClick={() => setOpened(true)}>
              Open
            </button>
          </div>
        ) : null}
        <div className="flyer-start-body">
          <p className="flyer-start-copy">Already have a flyer? Upload it and we&apos;ll fill in the form for you.</p>
          <label htmlFor={INPUT_ID} className="button flyer-start-button">
            Upload a flyer
          </label>
          <input
            id={INPUT_ID}
            className="flyer-start-input"
            type="file"
            accept={ACCEPT}
            onChange={event => {
              uploadFlyer(event.currentTarget.files?.[0])
              event.currentTarget.value = ''
            }}
          />
          <p className="flyer-start-hint">JPG, PNG, WebP, HEIC or PDF, up to 10 MB. You can also drop it here.</p>
        </div>
        {message && !(stopped && (phase === 'reading' || phase === 'uploading')) ? (
          <p className="flyer-start-status" role="status">
            {message}
          </p>
        ) : null}
      </StyledPanel>
      {notices ? <FlyerNotices notices={notices} /> : null}
    </StyledWrapper>
  )
}

// Below the panel, so shrinking it on a phone never hides one.
const FlyerNotices: React.FC<{ notices: Notices }> = ({ notices }) => {
  const lines = [
    notices.notAnEvent && NOT_AN_EVENT,
    notices.promotion && PROMOTION,
    notices.eventCount > 1 && severalEvents(notices.eventCount),
    notices.safety && SAFETY,
  ].filter((line): line is string => Boolean(line))
  if (!lines.length && !notices.duplicates.length) return null

  return (
    <StyledNotices role="status">
      {lines.map(line => (
        <p key={line}>{line}</p>
      ))}
      {notices.duplicates.length ? (
        <div>
          <p>{ALREADY_LISTED}</p>
          <ul>
            {notices.duplicates.map(duplicate => (
              <li key={duplicate.id}>
                <a href={duplicate.url || undefined} target="_blank" rel="noopener noreferrer">
                  {duplicate.title || 'An event'}
                  {duplicate.start_at ? ` (${dayjs(duplicate.start_at).format('MMM D, YYYY')})` : ''}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </StyledNotices>
  )
}

const StyledWrapper = styled.div`
  margin: 0 0 32px;
`

const StyledPanel = styled(FileDropBox)`
  padding: 20px;
  border: 1px dashed #959595;
  border-radius: 5px;
  text-align: center;

  .flyer-start-copy {
    margin: 0 0 12px;
    font-weight: 600;
  }

  .flyer-start-button {
    cursor: pointer;
  }

  .flyer-start-input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }

  .flyer-start-hint {
    margin: 12px 0 0;
    font-size: 14px;
    color: #6b7280;
  }

  .flyer-start-status {
    margin: 12px 0 0;
  }

  .flyer-start-summary {
    display: none;
  }

  @media (max-width: calc(${MEDIUM} - 1px)) {
    &.is-collapsed {
      padding: 12px 16px;
    }

    &.is-collapsed .flyer-start-summary {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      font-weight: 600;
    }

    &.is-collapsed .flyer-start-body {
      display: none;
    }
  }
`

const StyledNotices = styled.div`
  margin: 12px 0 0;
  padding: 12px 16px;
  border: 1px solid #fde68a;
  border-radius: 5px;
  background: #fffbeb;
  color: #92400e;

  p {
    margin: 0 0 8px;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul {
    margin: 0;
    padding-left: 20px;
  }
`

export default FlyerStartPanel
